<!-- CarePlan_ViewModule_Modal.vue -->
<template>
  <div class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="fixed inset-0 bg-gray-800-ds/60" @click="$emit('close')"></div>

      <div class="bg-white rounded-lg max-w-4xl w-full relative z-10 max-h-[90vh] overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="px-6 py-4 border-b" :style="{ borderTopWidth: '4px', borderTopColor: domainColor }">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold">{{ module.metadata?.title || 'Module Details' }}</h3>
              <div class="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                <span>
                  <FontAwesomeIcon :icon="getDomainIcon()" class="mr-1" :style="{ color: domainColor }" />
                  {{ threadType?.name }}
                </span>
                <span v-if="templateName">
                  Template: {{ templateName }}
                </span>
                <span>
                  Status:
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ml-1"
                    :class="getStatusClass(module.metadata?.status)"
                  >
                    {{ formatStatus(module.metadata?.status) }}
                  </span>
                </span>
              </div>
            </div>
            <button
              @click="$emit('close')"
              class="text-gray-400 hover:text-gray-600"
            >
              <FontAwesomeIcon :icon="['fas', 'times']" class="text-xl" />
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto">
          <!-- Module Information (Field Values) -->
          <div v-if="hasFieldValues" class="px-6 py-4 bg-gray-50 border-b">
            <h4 class="text-sm font-semibold text-gray-700 mb-4">Module Information</h4>
            <div v-if="fieldSchema && fieldSchema.length > 0">
              <!-- Render fields using schema -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div v-for="field in sortedFields" :key="field.id" class="space-y-1">
                  <label class="text-xs font-medium text-gray-500">{{ field.question }}</label>
                  <div class="text-sm text-gray-900">
                    {{ formatFieldValue(field, module.metadata.field_values[field.id]) }}
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              <!-- Fallback: show raw field values if no schema -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div v-for="(value, key) in module.metadata.field_values" :key="key" class="space-y-1">
                  <label class="text-xs font-medium text-gray-500">{{ formatFieldKey(key) }}</label>
                  <div class="text-sm text-gray-900">{{ formatRawValue(value) }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Goals Section -->
          <div class="px-6 py-4 border-b">
            <h4 class="text-sm font-semibold text-gray-700 mb-4">
              Goals ({{ goals.length }})
            </h4>
            <div v-if="goals.length === 0" class="text-sm text-gray-500">
              No goals defined for this module
            </div>
            <div v-else class="space-y-4">
              <div
                v-for="(goal, index) in goals"
                :key="goal.id || index"
                class="flex items-start space-x-4 p-4 bg-white rounded-lg border border-gray-200"
              >
                <div
                  class="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  :class="getGoalStatusStyle(goal.status)"
                >
                  <FontAwesomeIcon
                    :icon="getGoalStatusIcon(goal.status)"
                    class="text-xs"
                  />
                </div>
                <div class="flex-1">
                  <p class="text-sm text-gray-900">{{ goal.title }}</p>
                  <div class="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                    <span>Status: {{ formatStatus(goal.status) }}</span>
                    <span v-if="goal.measurement_type">Type: {{ goal.measurement_type }}</span>
                    <span v-if="goal.target_value">Target: {{ goal.target_value }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Interventions Section -->
          <div class="px-6 py-4 border-b">
            <h4 class="text-sm font-semibold text-gray-700 mb-4">
              Interventions ({{ interventions.length }})
            </h4>
            <div v-if="interventions.length === 0" class="text-sm text-gray-500">
              No interventions defined for this module
            </div>
            <div v-else class="space-y-4">
              <div
                v-for="(intervention, index) in interventions"
                :key="intervention.id || index"
                class="p-4 bg-white rounded-lg border border-gray-200"
              >
                <div class="flex items-start justify-between">
                  <div>
                    <p class="text-sm text-gray-900">{{ intervention.title }}</p>
                    <div class="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                      <span v-if="intervention.frequency">
                        <FontAwesomeIcon :icon="['fas', 'clock']" class="mr-1" />
                        {{ intervention.frequency }}
                      </span>
                      <span v-if="intervention.status">
                        Status: {{ formatStatus(intervention.status) }}
                      </span>
                      <span v-if="intervention.provider_type">
                        Provider: {{ intervention.provider_type }}
                      </span>
                    </div>
                  </div>
                  <span
                    v-if="intervention.status"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    :class="getInterventionStatusClass(intervention.status)"
                  >
                    {{ formatStatus(intervention.status) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Notes Section -->
          <div v-if="module.metadata?.notes" class="px-6 py-4 border-b">
            <h4 class="text-sm font-semibold text-gray-700 mb-4">Notes</h4>
            <p class="text-sm text-gray-900 whitespace-pre-wrap">{{ module.metadata.notes }}</p>
          </div>

          <!-- Engagement History -->
          <div v-if="module.metadata?.engagement_count > 0" class="px-6 py-4 border-b">
            <h4 class="text-sm font-semibold text-gray-700 mb-4">Engagement Summary</h4>
            <div class="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span class="text-gray-500">Total Engagements:</span>
                <span class="ml-2 font-medium">{{ module.metadata.engagement_count || 0 }}</span>
              </div>
              <div>
                <span class="text-gray-500">Total Time:</span>
                <span class="ml-2 font-medium">{{ formatDuration(module.metadata.total_time_spent) }}</span>
              </div>
              <div>
                <span class="text-gray-500">Last Engagement:</span>
                <span class="ml-2 font-medium">{{ formatDate(module.metadata.last_touchpoint) }}</span>
              </div>
            </div>
          </div>

          <!-- Module Metadata -->
          <div class="px-6 py-4 bg-gray-50">
            <h4 class="text-sm font-semibold text-gray-700 mb-4">Module Details</h4>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span class="text-gray-500">Created:</span>
                <span class="ml-2">{{ formatDate(module.created_at) }}</span>
              </div>
              <div>
                <span class="text-gray-500">Last Updated:</span>
                <span class="ml-2">{{ formatDate(module.updated_at) }}</span>
              </div>
              <div v-if="module.metadata?.typical_duration_weeks">
                <span class="text-gray-500">Expected Duration:</span>
                <span class="ml-2">{{ module.metadata.typical_duration_weeks }} weeks</span>
              </div>
              <div>
                <span class="text-gray-500">Module ID:</span>
                <span class="ml-2 font-mono text-xs">{{ module.id }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="px-6 py-4 border-t flex justify-end">
          <button
            @click="$emit('close')"
            class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'CarePlan_ViewModule_Modal',

  components: {
    FontAwesomeIcon
  },

  props: {
    module: {
      type: Object,
      required: true
    },
    threadType: {
      type: Object,
      default: null
    }
  },

  computed: {
    domainColor() {
      return this.threadType?.metadata?.domain_config?.domain_color || '#6366F1';
    },

    templateName() {
      if (!this.module.metadata?.template_used || !this.threadType?.metadata?.module_templates) {
        return null;
      }
      const template = this.threadType.metadata.module_templates.find(
        t => t.template_id === this.module.metadata.template_used
      );
      return template?.name || this.module.metadata.template_used;
    },

    fieldSchema() {
      // Check if module has stored field schema
      if (this.module.metadata?.field_schema?.fields) {
        return this.module.metadata.field_schema.fields;
      }
      // Otherwise get from template
      if (this.module.metadata?.template_used && this.threadType?.metadata?.module_templates) {
        const template = this.threadType.metadata.module_templates.find(
          t => t.template_id === this.module.metadata.template_used
        );
        return template?.fields || null;
      }
      return null;
    },

    sortedFields() {
      if (!this.fieldSchema) return [];
      return [...this.fieldSchema].sort((a, b) => {
        const orderA = a.order !== undefined ? a.order : 999;
        const orderB = b.order !== undefined ? b.order : 999;
        return orderA - orderB;
      });
    },

    hasFieldValues() {
      return this.module.metadata?.field_values &&
             Object.keys(this.module.metadata.field_values).length > 0;
    },

    goals() {
      return this.module.metadata?.goals || [];
    },

    interventions() {
      return this.module.metadata?.interventions || [];
    }
  },

  methods: {
    getDomainIcon() {
      const icon = this.threadType?.metadata?.domain_config?.domain_icon;
      if (!icon) return ['fas', 'layer-group'];

      if (typeof icon === 'string') {
        return ['fas', icon];
      }
      return icon;
    },

    getStatusClass(status) {
      const statusClasses = {
        'active': 'bg-green-100 text-green-800',
        'planned': 'bg-blue-100 text-blue-800',
        'pending': 'bg-yellow-100 text-yellow-800',
        'on_hold': 'bg-orange-100 text-orange-800',
        'completed': 'bg-gray-100 text-gray-800',
        'discontinued': 'bg-red-100 text-red-800'
      };
      return statusClasses[status] || 'bg-gray-100 text-gray-800';
    },

    getGoalStatusStyle(status) {
      const styles = {
        'completed': 'bg-green-100 text-green-600',
        'in_progress': 'bg-yellow-100 text-yellow-600',
        'not_started': 'bg-gray-100 text-gray-600',
        'blocked': 'bg-red-100 text-red-600'
      };
      return styles[status] || 'bg-gray-100 text-gray-600';
    },

    getGoalStatusIcon(status) {
      const icons = {
        'completed': ['fas', 'check'],
        'in_progress': ['fas', 'spinner'],
        'not_started': ['fas', 'circle'],
        'blocked': ['fas', 'times']
      };
      return icons[status] || ['fas', 'circle'];
    },

    getInterventionStatusClass(status) {
      const classes = {
        'active': 'bg-green-100 text-green-800',
        'planned': 'bg-blue-100 text-blue-800',
        'completed': 'bg-gray-100 text-gray-800',
        'discontinued': 'bg-red-100 text-red-800'
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    },

    formatStatus(status) {
      if (!status) return 'Unknown';
      return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    },

    formatDuration(minutes) {
      if (!minutes) return 'N/A';
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hours > 0) {
        return `${hours}h ${mins}m`;
      }
      return `${mins}m`;
    },

    formatFieldKey(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    formatFieldValue(field, value) {
      if (value === null || value === undefined || value === '') {
        return 'Not provided';
      }

      // Handle different field types
      switch (field.type) {
        case 'boolean':
          return value ? 'Yes' : 'No';

        case 'checkbox':
          if (Array.isArray(value) && value.length > 0) {
            // If field has options, map values to labels
            if (field.options) {
              return value.map(v => {
                const option = field.options.find(o => o.value === v);
                return option ? option.label : v;
              }).join(', ');
            }
            return value.join(', ');
          }
          return 'None selected';

        case 'radio':
        case 'select':
          if (field.options) {
            const option = field.options.find(o => o.value === value);
            return option ? option.label : value;
          }
          return value;

        case 'scale':
          return `${value} / ${field.validation?.max || 10}`;

        case 'date':
          return this.formatDate(value);

        default:
          return value.toString();
      }
    },

    formatRawValue(value) {
      if (value === null || value === undefined) return 'N/A';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'object') return JSON.stringify(value);
      if (Array.isArray(value)) return value.join(', ');
      return value.toString();
    }
  }
};
</script>

<style scoped>
/* Any custom styles if needed */
</style>
